import React, { useEffect } from "react";
import { useTranslation } from "src/_common/hooks/useTranslation";

interface PaymentForm {
  cardNumber: string;
  expiryDate: string;
  cvc: string;
  nameOnCard: string;
  country: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  stateRegion: string;
  setAsDefault: boolean;
}

interface PaymentSidebarProps {
  isOpen: boolean;
  paymentForm: PaymentForm;
  onClose: () => void;
  onFormChange: (field: string, value: string | boolean) => void;
  onSubmit: () => void;
}

const PaymentSidebar: React.FC<PaymentSidebarProps> = ({ isOpen, paymentForm, onClose, onFormChange, onSubmit }) => {
  const { t } = useTranslation("settings");

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="payment-sidebar-overlay">
      <div className="payment-sidebar">
        <div className="payment-sidebar-header">
          <h3>{t("billing.paymentSidebar.title")}</h3>
          <button className="payment-sidebar-close" onClick={onClose}>
            <i className="fa fa-times"></i>
          </button>
        </div>

        <div className="payment-sidebar-body">
          <p className="payment-sidebar-description">
            {t("billing.paymentSidebar.description")
              .split("\n")
              .map((line: string, i: number) => (
                <React.Fragment key={i}>
                  {line}
                  {i < t("billing.paymentSidebar.description").split("\n").length - 1 && <br />}
                </React.Fragment>
              ))}
          </p>

          <div className="payment-form">
            <div className="payment-form-section">
              <h4>{t("billing.paymentSidebar.sections.cardInfo")}</h4>

              <div className="payment-form-row">
                <div className="payment-form-field full-width">
                  <input
                    type="text"
                    placeholder={t("billing.paymentSidebar.placeholders.cardNumber")}
                    value={paymentForm.cardNumber}
                    onChange={(e) => onFormChange("cardNumber", e.target.value)}
                    className="payment-input"
                  />
                </div>
              </div>

              <div className="payment-form-row">
                <div className="payment-form-field">
                  <input
                    type="text"
                    placeholder={t("billing.paymentSidebar.placeholders.expiryDate")}
                    value={paymentForm.expiryDate}
                    onChange={(e) => onFormChange("expiryDate", e.target.value)}
                    className="payment-input"
                  />
                </div>
                <div className="payment-form-field">
                  <input
                    type="text"
                    placeholder={t("billing.paymentSidebar.placeholders.cvc")}
                    value={paymentForm.cvc}
                    onChange={(e) => onFormChange("cvc", e.target.value)}
                    className="payment-input"
                  />
                </div>
              </div>

              <div className="payment-form-row">
                <div className="payment-form-field full-width">
                  <input
                    type="text"
                    placeholder={t("billing.paymentSidebar.placeholders.fullName")}
                    value={paymentForm.nameOnCard}
                    onChange={(e) => onFormChange("nameOnCard", e.target.value)}
                    className="payment-input"
                  />
                </div>
              </div>
            </div>

            <div className="payment-form-section">
              <h4>{t("billing.paymentSidebar.sections.billingAddress")}</h4>

              <div className="payment-form-row">
                <div className="payment-form-field full-width">
                  <select
                    value={paymentForm.country}
                    onChange={(e) => onFormChange("country", e.target.value)}
                    className="payment-select"
                  >
                    <option value="">{t("billing.paymentSidebar.placeholders.selectCountry")}</option>
                    <option value="US">{t("billing.paymentSidebar.countries.us")}</option>
                    <option value="UAE">{t("billing.paymentSidebar.countries.uae")}</option>
                    <option value="CA">{t("billing.paymentSidebar.countries.ca")}</option>
                    <option value="UK">{t("billing.paymentSidebar.countries.uk")}</option>
                  </select>
                  <i className="fa fa-chevron-down payment-select-arrow"></i>
                </div>
              </div>

              <div className="payment-form-row">
                <div className="payment-form-field full-width">
                  <input
                    type="text"
                    placeholder={t("billing.paymentSidebar.placeholders.addressLine1")}
                    value={paymentForm.addressLine1}
                    onChange={(e) => onFormChange("addressLine1", e.target.value)}
                    className="payment-input"
                  />
                </div>
              </div>

              <div className="payment-form-row">
                <div className="payment-form-field full-width">
                  <input
                    type="text"
                    placeholder={t("billing.paymentSidebar.placeholders.addressLine2")}
                    value={paymentForm.addressLine2}
                    onChange={(e) => onFormChange("addressLine2", e.target.value)}
                    className="payment-input"
                  />
                </div>
              </div>

              <div className="payment-form-row">
                <div className="payment-form-field">
                  <input
                    type="text"
                    placeholder={t("billing.paymentSidebar.placeholders.city")}
                    value={paymentForm.city}
                    onChange={(e) => onFormChange("city", e.target.value)}
                    className="payment-input"
                  />
                </div>
                <div className="payment-form-field">
                  <input
                    type="text"
                    placeholder={t("billing.paymentSidebar.placeholders.postalCode")}
                    value={paymentForm.postalCode}
                    onChange={(e) => onFormChange("postalCode", e.target.value)}
                    className="payment-input"
                  />
                </div>
              </div>

              <div className="payment-form-row">
                <div className="payment-form-field full-width">
                  <input
                    type="text"
                    placeholder={t("billing.paymentSidebar.placeholders.stateRegion")}
                    value={paymentForm.stateRegion}
                    onChange={(e) => onFormChange("stateRegion", e.target.value)}
                    className="payment-input"
                  />
                </div>
              </div>
            </div>

            <div className="payment-form-checkbox">
              <label className="payment-checkbox-label">
                <input
                  type="checkbox"
                  checked={paymentForm.setAsDefault}
                  onChange={(e) => onFormChange("setAsDefault", e.target.checked)}
                  className="payment-checkbox"
                />
                <span className="payment-checkbox-text">{t("billing.paymentSidebar.setAsDefault")}</span>
              </label>
            </div>
          </div>
        </div>

        <div className="payment-sidebar-footer">
          <button className="btn-cancel" onClick={onClose}>
            {t("billing.paymentSidebar.buttons.cancel")}
          </button>
          <button className="btn-preview" onClick={onSubmit}>
            {t("billing.paymentSidebar.buttons.add")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSidebar;
